import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect, useLayoutEffect, useRef } from "react";
import { useCompConfig, applyCompConfigCssVars } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import BoltMatchLeagues, {
  BOLT_V26_COLS,
  BOLT_V26_KETQUA_COLS,
} from "./BoltMatchLeagues";
import BoltMatch from "./BoltMatch";
import BoltDateTabs from "./BoltDateTabs";
import LoadMoreButton from "../LoadMoreButton";
import LeagueFilterV26 from "./LeagueFilterV26";
import "./index.css";
import { BANG_KEO_PAGE_LIMIT, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { buildThemeStyle } from "./buildThemeStyle";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const getTodayString = () => dayjs().tz(TZ).format("YYYY-MM-DD");

const getLeaguesFromData = (items = []) => {
  const seen = new Set();
  const leagues = [];
  for (const item of items) {
    const league = item?.league;
    if (!league) continue;
    const id = String(league.id || league.name || "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    leagues.push({
      id: league.id || league.name,
      name: league.name || league.nameVN,
      nameVN: league.nameVN,
      logo: league.logo,
    });
  }
  return leagues;
};

const isKetquaType = (type) =>
  typeof type === "string" && type.includes("ketqua");

const KbvTiLeV26 = ({ type = "all" }) => {
  const isKetqua = isKetquaType(type);

  const wrapRef = useRef(null);
  const compConfig = useCompConfig();
  const { hasDataConfig, parsedConfig } = compConfig;
  const themeStyle = buildThemeStyle(compConfig, hasDataConfig);

  useLayoutEffect(() => {
    const root = wrapRef.current?.closest(".keobongvip-root");
    if (root && hasDataConfig) applyCompConfigCssVars(root, parsedConfig);
  }, [hasDataConfig, parsedConfig]);

  const [selectedDate, setSelectedDate] = useState(getTodayString);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState(null);
  const [displayLimit, setDisplayLimit] = useState(20);

  const todayString = getTodayString();

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status = isKetqua
    ? "end"
    : selectedDate === "live"
      ? "live"
      : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filterLeagues = useMemo(
    () => getLeaguesFromData(baseLeaguesWithMatches),
    [baseLeaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (selectedLeagueIds === null) return baseLeaguesWithMatches;
    return baseLeaguesWithMatches.filter((item) =>
      selectedLeagueIds.some(
        (id) => String(id) === String(item.league?.id || item.league?.name),
      ),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  const visibleMatches = useMemo(() => {
    return allFilteredMatches.slice(0, displayLimit);
  }, [allFilteredMatches, displayLimit]);

  const groupedVisibleMatches = useMemo(() => {
    const groups = [];
    visibleMatches.forEach((match) => {
      const leagueId = String(match.league?.id || match.league?.name || "");
      let group = groups.find(
        (g) => String(g.league?.id || g.league?.name || "") === leagueId,
      );
      if (!group) {
        group = { league: match.league, matches: [] };
        groups.push(group);
      }
      group.matches.push(match);
    });
    return groups;
  }, [visibleMatches]);

  useEffect(() => {
    // Auto-fetch if filtered results are below display limit
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  const handleLeagueToggle = (id) => {
    const nextId = String(id);
    setSelectedLeagueIds((prev) => {
      if (prev === null) return [nextId];
      const has = prev.some((x) => String(x) === nextId);
      const next = has ? prev.filter((x) => String(x) !== nextId) : [...prev, nextId];
      if (next.length === 0 || next.length === filterLeagues.length) return null;
      return next;
    });
    setDisplayLimit(20);
  };

  const handleLeagueClear = () => {
    setSelectedLeagueIds(null);
    setDisplayLimit(20);
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setSelectedLeagueIds(null);
    setDisplayLimit(20);
  };

  return (
    <div
      ref={wrapRef}
      className={`mx-auto flex min-h-screen min-w-0 w-full flex-col gap-3 rounded-xl px-0 md:px-2 py-3 bg-[var(--kbv-bg-page)]${
        isKetqua ? " kbv-v26-is-ketqua" : ""
      }`}
      style={themeStyle}
    >
      <div className="flex flex-col gap-3">
        <div className="kbv-v26-date-filter">
          <BoltDateTabs
            activeTab={selectedDate || todayString}
            onChange={handleDateChange}
            direction={isKetqua ? "backward" : "forward"}
          />
        </div>
        <LeagueFilterV26
          leagues={filterLeagues}
          selectedIds={selectedLeagueIds}
          onToggle={handleLeagueToggle}
          onClear={handleLeagueClear}
          themeStyle={themeStyle}
          loading={loading}
        />
      </div>

      {loading && <Loading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          <div className="rounded-lg">
            <div className="rounded-lg">
              {groupedVisibleMatches.length === 0 ? (
                <NoData
                  message={
                    isLoadingMore
                      ? isKetqua
                        ? "Đang tìm thêm kết quả..."
                        : "Đang tìm thêm trận đấu phù hợp..."
                      : isKetqua
                        ? "Không có trận đấu nào"
                        : "Không tìm thấy trận đấu theo tiêu chí lọc"
                  }
                />
              ) : (
                <div className="kbv-v26-odds-table min-w-0 max-w-full overflow-x-auto bg-transparent md:bg-[var(--theme-bg)] border-0 md:border md:border-[var(--theme-border)] rounded-none md:rounded-lg">
                  <div
                    className={`hidden md:grid ${isKetqua ? BOLT_V26_KETQUA_COLS : BOLT_V26_COLS} items-center w-full min-w-0 gap-x-[20px] h-[40px] px-2 bg-[var(--theme-bg-muted)] border-b border-[var(--theme-border)] text-[14px] text-[var(--theme-text-muted)] font-bold md:rounded-t-lg`}
                  >
                    <div />
                    <div />
                    <div className="kbv-v26-market-head min-w-0 text-center leading-tight">Kèo chấp (HT/FT)</div>
                    <div className="kbv-v26-market-head min-w-0 text-center leading-tight">Kèo tài xỉu (HT/FT)</div>
                    <div className="kbv-v26-market-head kbv-v26-market-head-1x2 min-w-0 text-center leading-tight whitespace-nowrap">1x2 (HT/FT)</div>
                    {isKetqua ? null : <div />}
                  </div>
                  {groupedVisibleMatches.map(({ league, matches }, leagueIndex) => (
                    <BoltMatchLeagues
                      key={`${league?.id ?? league?.name ?? "lg"}-${leagueIndex}`}
                      league={league}
                      matches={matches}
                      render={(match, idx) =>
                        match ? (
                          <BoltMatch
                            key={match.id || `match-${idx}`}
                            match={match}
                            index={idx}
                            isKetqua={isKetqua}
                          />
                        ) : null
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={() => setDisplayLimit((prev) => prev + 20)}
            loadingComponent={<Loading />}
            hasMore={allFilteredMatches.length > displayLimit || hasMore}
          />
        </>
      )}
    </div>
  );
};

export function KbvTiLeV26KetQua(props) {
  return <KbvTiLeV26 {...props} type="ketqua" />;
}

export function KbvTiLeV26KetQuaDark(props) {
  return <KbvTiLeV26 {...props} type="ketquadark" />;
}

export default KbvTiLeV26;
